import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Droppable } from 'react-beautiful-dnd';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import FacetRow from './FacetRow';
import styles from './FiltersBoard.module.scss';

const useFilter = (items, query) => useMemo(() => {
  if (!query) return items;
  const normalizedQuery = String(query).toLowerCase();
  return items.filter((facet) => String(facet.title || '').toLowerCase().includes(normalizedQuery)
      || String(facet.id).toLowerCase().includes(normalizedQuery));
}, [items, query]);

const ZoneSection = ({
  droppableId,
  title,
  subtitle,
  items,
  onEdit,
  onEditValues,
  onUpdate,
  dragDisabled,
  searchTerm,
  localizeMessage,
}) => {
  const visible = useFilter(items, searchTerm);
  const emptyMessage = searchTerm
    ? localizeMessage('customization.filters.board.noMatches')
    : localizeMessage(`customization.filters.board.${droppableId}.empty`);

  return (
    <section
      className={`${styles.section} ${styles[`section_${droppableId}`]}`}
      data-testid={`facet-zone-${droppableId}`}
    >
      <div className={styles.sectionHeader}>
        <div>
          <div className={styles.sectionTitleRow}>
            <h2 className={styles.sectionTitle}>{title}</h2>
            <span className={styles.zoneCount}>{visible.length}</span>
          </div>
          <p className={styles.sectionSubtitle}>{subtitle}</p>
        </div>
      </div>
      <div className={styles.sectionBody}>
        {visible.length > 0 && (
          <div className={styles.headerGrid} aria-hidden="true">
            <div />
            <div>{localizeMessage('customization.filters.table.title')}</div>
            <div>{localizeMessage('customization.filters.table.type')}</div>
            <div>{localizeMessage('customization.filters.table.order')}</div>
            <div className={styles.centeredHeader}>{localizeMessage('customization.filters.board.visible')}</div>
            <div className={styles.actionsHeader}>{localizeMessage('customization.filters.table.actions')}</div>
          </div>
        )}
        <Droppable droppableId={droppableId} isDropDisabled={false}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={`${styles.list} ${snapshot.isDraggingOver ? styles.listDraggingOver : ''}`.trim()}
              data-zone-list={droppableId}
            >
              {visible.map((facet, index) => {
                const dragId = String(facet._cid);
                return (
                  <FacetRow
                    key={dragId}
                    dragId={dragId}
                    facet={facet}
                    index={index}
                    onEdit={onEdit}
                    onEditValues={onEditValues}
                    onUpdate={onUpdate}
                    dragDisabled={Boolean(dragDisabled)}
                  />
                );
              })}
              {visible.length === 0 && (
                <div className={styles.emptyZone} data-testid={`facet-zone-${droppableId}-empty`}>
                  {emptyMessage}
                </div>
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </div>
    </section>
  );
};

ZoneSection.propTypes = {
  droppableId: PropTypes.oneOf(['head', 'nat', 'tail']).isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  onEdit: PropTypes.func,
  onEditValues: PropTypes.func,
  onUpdate: PropTypes.func.isRequired,
  dragDisabled: PropTypes.bool.isRequired,
  searchTerm: PropTypes.string.isRequired,
  localizeMessage: PropTypes.func.isRequired,
};

ZoneSection.defaultProps = {
  onEdit: null,
  onEditValues: null,
};

export default function FiltersBoard({
  head,
  nat,
  tail,
  onEdit,
  onEditValues,
  onUpdate,
  dndEnabled,
  searchTerm,
}) {
  const localizeMessage = useLocalizedMessage();
  const dragDisabled = !dndEnabled;

  return (
    <div className={styles.board} data-testid="filters-board">
      <ZoneSection
        droppableId="head"
        title={localizeMessage('customization.filters.board.head.title')}
        subtitle={localizeMessage('customization.filters.board.head.subtitle')}
        items={head}
        onEdit={onEdit}
        onEditValues={onEditValues}
        onUpdate={onUpdate}
        dragDisabled={dragDisabled}
        searchTerm={searchTerm}
        localizeMessage={localizeMessage}
      />
      <ZoneSection
        droppableId="nat"
        title={localizeMessage('customization.filters.board.nat.title')}
        subtitle={localizeMessage('customization.filters.board.nat.subtitle')}
        items={nat}
        onEdit={onEdit}
        onEditValues={onEditValues}
        onUpdate={onUpdate}
        dragDisabled={dragDisabled}
        searchTerm={searchTerm}
        localizeMessage={localizeMessage}
      />
      <ZoneSection
        droppableId="tail"
        title={localizeMessage('customization.filters.board.tail.title')}
        subtitle={localizeMessage('customization.filters.board.tail.subtitle')}
        items={tail}
        onEdit={onEdit}
        onEditValues={onEditValues}
        onUpdate={onUpdate}
        dragDisabled={dragDisabled}
        searchTerm={searchTerm}
        localizeMessage={localizeMessage}
      />
    </div>
  );
}

FiltersBoard.propTypes = {
  head: PropTypes.arrayOf(PropTypes.object).isRequired,
  nat: PropTypes.arrayOf(PropTypes.object).isRequired,
  tail: PropTypes.arrayOf(PropTypes.object).isRequired,
  onEdit: PropTypes.func,
  onEditValues: PropTypes.func,
  onUpdate: PropTypes.func.isRequired,
  dndEnabled: PropTypes.bool.isRequired,
  searchTerm: PropTypes.string.isRequired,
};

FiltersBoard.defaultProps = {
  onEdit: null,
  onEditValues: null,
};
